import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, RefreshCw, DollarSign, Package, Lightbulb, Radio, ExternalLink, Users, Pencil, Check } from "lucide-react";
import { ChipInput } from "@/components/onboarding/ChipInput";
import { useAccountKnowledge } from "./useAccountKnowledge";
import { relationshipBadge } from "./accountsModel";

const Section = ({ icon: Icon, title, children, testId, action }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-2" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-2"><Icon className="h-3.5 w-3.5 text-ism-teal" /> {title}</p>
      {action}
    </div>
    {children}
  </div>
);

const CONF = { high: "text-emerald-300", medium: "text-amber-300", low: "text-gray-400" };
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);
const fmtDate = (d) => { const t = Date.parse(d); return Number.isNaN(t) ? "" : new Date(t).toLocaleDateString(); };

const SpendEditor = ({ spend, onSave, saving }) => {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(spend?.annual ?? "");
  const [basis, setBasis] = useState(spend?.basis || "");
  if (!edit) {
    return (
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-lg text-white" data-testid="knowledge-spend-value">{money(spend?.annual)}<span className="text-xs text-gray-500"> / yr</span></p>
          {spend?.basis && <p className="text-[11px] text-gray-500">{spend.basis}</p>}
        </div>
        <button onClick={() => setEdit(true)} className="text-gray-500 hover:text-white" aria-label="Edit spend" data-testid="knowledge-spend-edit"><Pencil className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Annual spend" className="w-36 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" data-testid="knowledge-spend-input" />
      <input value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Basis (e.g. 2025 contract)" className="flex-1 min-w-[160px] bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" data-testid="knowledge-spend-basis" />
      <Button size="sm" disabled={saving} onClick={async () => { if (await onSave({ spendAnnual: val === "" ? null : Number(val), spendBasis: basis })) setEdit(false); }} className="bg-ism-teal text-black h-8" data-testid="knowledge-spend-save"><Check className="h-3.5 w-3.5" /></Button>
    </div>
  );
};

export const AccountKnowledgePanel = ({ accountId, onChanged }) => {
  const k = useAccountKnowledge(accountId, onChanged);
  if (k.loading) return <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-ism-teal mx-auto" /></div>;
  if (!k.data) return null;
  const kn = k.data.knowledge || {};
  const rel = k.data.relationship;

  return (
    <div className="space-y-4" data-testid="account-knowledge-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm text-gray-400 flex items-center gap-2"><Sparkles className="h-4 w-4 text-ism-teal/70" /> Customer knowledge</h3>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-white/10 overflow-hidden" data-testid="knowledge-relationship-toggle">
            {["customer", "wishlist"].map((r) => (
              <button key={r} onClick={() => rel !== r && k.save({ relationship: r })} className={`text-[11px] px-3 py-1 capitalize ${rel === r ? relationshipBadge(r) : "text-gray-500 hover:text-gray-300"}`} data-testid={`knowledge-rel-${r}`}>{r}</button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={k.enrich} disabled={k.enriching} className="border-ism-teal/30 text-ism-teal hover:bg-ism-teal/10 h-8" data-testid="knowledge-enrich-btn">
            {k.enriching ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} {kn.enrichedAt ? "Refresh" : "Research this account"}
          </Button>
        </div>
      </div>

      {kn.summary ? <p className="text-sm text-gray-300 leading-relaxed" data-testid="knowledge-summary">{kn.summary}</p>
        : <p className="text-sm text-gray-500" data-testid="knowledge-empty">Nothing researched yet. Run research to learn what {k.data.companyName} does, what they likely need from you, and what&apos;s changing there.</p>}

      <div className="grid gap-4 md:grid-cols-2">
        <Section icon={DollarSign} title="Annual spend with you" testId="knowledge-spend">
          <SpendEditor spend={kn.spend} onSave={k.save} saving={k.saving} />
        </Section>
        <Section icon={Package} title="Products they own" testId="knowledge-owned">
          <ChipInput values={kn.productsOwned || []} onChange={(v) => k.save({ productsOwned: v })} placeholder="Add a product they already use" testId="knowledge-owned-chips" />
        </Section>
      </div>

      <Section icon={Lightbulb} title={rel === "customer" ? "Likely needs (expansion)" : "Likely needs (your opening)"} testId="knowledge-needs">
        {(kn.productsNeeded || []).length ? (
          <ul className="space-y-2">
            {kn.productsNeeded.map((n) => (
              <li key={n.product} className="text-sm" data-testid="knowledge-need-item">
                <span className="text-white">{n.product}</span> <span className={`text-[10px] uppercase ml-1 ${CONF[n.confidence] || CONF.low}`}>{n.confidence}</span>
                {n.reason && <p className="text-xs text-gray-500">{n.reason}</p>}
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-gray-500">No gaps identified yet.</p>}
      </Section>

      {(kn.signals || []).length > 0 && (
        <Section icon={Radio} title="Recent signals" testId="knowledge-signals">
          <ul className="space-y-1.5">
            {kn.signals.map((s) => (
              <li key={s.text} className="text-sm text-gray-300 flex items-start gap-2">
                <span className="flex-1">{s.text}{s.date && fmtDate(s.date) && <span className="text-xs text-gray-500"> · {fmtDate(s.date)}</span>}</span>
                {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-ism-teal hover:text-ism-teal-light flex-shrink-0" data-testid="knowledge-signal-link"><ExternalLink className="h-3.5 w-3.5" /></a>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {kn.nextBestAction && (
        <div className="bg-ism-teal/5 border border-ism-teal/20 rounded-lg p-4" data-testid="knowledge-next-action">
          <p className="text-xs uppercase tracking-wider text-ism-teal mb-1">Next best action</p>
          <p className="text-sm text-gray-200">{kn.nextBestAction}</p>
        </div>
      )}

      <Section icon={Users} title={`People (${k.data.contacts.length})`} testId="knowledge-contacts">
        {k.data.contacts.length ? (
          <div className="flex flex-wrap gap-2">
            {k.data.contacts.map((c) => (
              <Badge key={c.leadId} className="bg-white/5 text-gray-200 border-white/10 border font-normal">{c.contactName}{c.contactTitle ? ` · ${c.contactTitle}` : ""}</Badge>
            ))}
          </div>
        ) : <p className="text-xs text-gray-500">No contacts linked yet — add them via “Add customers &amp; prospects”.</p>}
      </Section>

      {kn.enrichedAt && <p className="text-[11px] text-gray-600">Last researched {new Date(kn.enrichedAt).toLocaleString()} · {(kn.sources || []).length} sources</p>}
    </div>
  );
};
